import { Dispatch, FC, SetStateAction, useMemo } from "react";
import { GridApi } from "ag-grid-community";
import { usePreviousDifferent } from "rooks";
import { grey } from "@mui/material/colors";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerGroupingSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerGroupingSelector";
import {
  AgDataGrid,
  AgDataGridProps,
} from "@/components/pages/common/ag-grid/AgDataGrid";
import { resourceExplorerGranularViewGridDataSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data-grid/granular-costs-view/resourceExplorerGranularViewGridDataSelector";
import { resourceExplorerGranularViewGridColumnKeysSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data-grid/granular-costs-view/resourceExplorerGranularViewGridColumnKeysSelector";
import { getBillingExplorerGranularViewColumnDefs } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/column-defs/billingExplorerGranularViewColumnDefs";

interface BillingExplorerTotalViewGridProps {
  setGridApi: Dispatch<SetStateAction<GridApi | null>>;
  rowHoverProps: Pick<AgDataGridProps, "onCellMouseOver" | "onCellMouseOut">;
}

export const BillingExplorerGranularViewGrid: FC<
  BillingExplorerTotalViewGridProps
> = ({ setGridApi, rowHoverProps }) => {
  const gridData = useAppSelector(resourceExplorerGranularViewGridDataSelector);
  const columns = useAppSelector(
    resourceExplorerGranularViewGridColumnKeysSelector,
  );
  const grouping = useAppSelector(resourceExplorerGroupingSelector);
  const prevData = usePreviousDifferent(gridData);

  const columnDefs = useMemo(() => {
    return getBillingExplorerGranularViewColumnDefs(grouping, columns);
  }, [columns, grouping]);

  const data = gridData ?? prevData;

  if (!data) {
    return null;
  }

  return (
    <AgDataGrid
      key={grouping}
      rowData={data}
      headerHeight={60}
      rowHeight={50}
      columnDefs={columnDefs}
      configToRememberStateInStorage={configToRememberStateInStorage}
      gridApiSetter={setGridApi}
      defaultColDef={defaultColDef}
      wrapperStyles={wrapperStyles}
      {...rowHoverProps}
    />
  );
};

const configToRememberStateInStorage = {
  uniqueNameAsKey: "billing-explorer-granular-view",
};

const wrapperStyles = {
  border: 1,
  minHeight: 500,
  borderColor: grey[300],
  ".ag-header-cell-text": { width: "100%" },
};

const defaultColDef = {
  minWidth: 150,
  unSortIcon: true,
  flex: 1,
};
